// The night sky. It used to be 2,000 identical square dots, every one the
// same size and near enough the same brightness, hung on a shell nine and a
// half kilometres round the map's centre — so a pilot out over the map's edge
// flew half of them past the far plane, and the whole dome turned on the spot
// about the zenith like a lampshade. Now it is a sky a navigator could use:
//
//   * A few bright stars and a great many faint ones, the way the real sky
//     runs — each step of a magnitude fainter there are about three times as
//     many. The brightest are fat and soft, the faintest a single pinprick.
//   * They have colours: blue-white, white, the sun's own yellow, orange and
//     the odd red giant, pale as starlight is.
//   * They come out in order. At dusk the brightest pierce the afterglow
//     first, the rest fill in as the sky darkens; a bright moon washes the
//     faintest back out, and an overcast takes all but a few.
//   * They twinkle — the air they come through shivers them — and they
//     twinkle hardest low down, where there is most air in the way. Low stars
//     are dimmer too, for the same reason.
//   * The whole sky wheels once a day about a pole star that hangs still
//     low in the north, on the same axis the sun and the moon ride, so the
//     stars rise where the sun rises and set where it sets.
//   * The Milky Way is a glow, not a scatter of dots: a clumpy river of light
//     with a dark rift down its middle, grained with faint stars.
//
// Every star is placed by its shader at the far plane in the direction it
// lies, like the sun (sun.js): it never parallaxes, never clips, and a ridge
// or a cloud in front of it hides it.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";

// The sky's pole: the axis the sun's day-path (world.js buildSky) turns
// about — its circle is centred on (0, 0.14, 0.34) — so ~22° up over +z.
export const CELESTIAL_POLE = new THREE.Vector3(0, 0.14, 0.34).normalize();

// Magnitudes: the brightest star in the sky, and the faintest the eye picks
// out on a clear moonless night.
const MAG_BRIGHT = -1.4;
const MAG_FAINT = 5.8;
// How fast the count grows toward faint stars (per magnitude, as a power of
// ten: the real sky's ~0.5, a touch shallower so the screen has bright stars
// enough to steer by).
const MAG_SLOPE = 0.42;
const FIELD_STARS = 7000; // over the whole sphere — about half are up
const BAND_STARS = 2600; // the Milky Way's faint grain
// Screen brightness of a first-magnitude star; one magnitude fainter is this
// many times dimmer on screen. The eye compresses starlight — a real
// magnitude is 2.5× — or the faint half of the sky would vanish on a phone.
const MAG_STEP = 1.45;
// The Milky Way's plane (its pole, in the sky's own frame) and how thick its
// glow is either side, in radians.
const GALACTIC_POLE = new THREE.Vector3(0.62, 0.3, -0.72).normalize();
const BAND_HALF = 0.28;
// How bright the Milky Way's glow gets at its densest, as a screen brightness
// over black: it should be something you notice on a dark night, not a stripe.
const BAND_GLOW = 0.26;

// Tier 2's starlight curve (see `linear` below).
const LINEAR_GAMMA = 1.5;

// Star colours by spectral class, pale as starlight looks, and how common
// each is among the naked-eye stars.
const SPECTRA = [
  [0xb5ccff, 0.1], // O/B: blue-white
  [0xd8e3ff, 0.22], // A: white with a breath of blue
  [0xfbf8ff, 0.22], // F: white
  [0xfff3dc, 0.2], // G: the sun's own yellow
  [0xffddb0, 0.19], // K: orange
  [0xffc69a, 0.07], // M: red giants
];

/**
 * A star's magnitude from a uniform draw `u` in [0,1): inverse of a count
 * that grows by 10^MAG_SLOPE per magnitude, so most of the sky is faint.
 */
export function starMagnitude(u) {
  const a = Math.pow(10, MAG_SLOPE * MAG_BRIGHT);
  const b = Math.pow(10, MAG_SLOPE * MAG_FAINT);
  return Math.log10(a + u * (b - a)) / MAG_SLOPE;
}

/**
 * The faintest magnitude that shows: `nightK` 0 (dusk) .. 1 (full dark),
 * `moon` 0..1 how much bright moonlight is up, `gray` the round's overcast.
 */
export function limitingMagnitude(nightK, moon, gray) {
  return MAG_BRIGHT + (MAG_FAINT + 0.4 - MAG_BRIGHT) * Math.sqrt(Math.max(0, nightK))
    - 0.5 * moon - 5.5 * gray;
}

// Smooth value noise over the band (along it, across it) and a few octaves
// of it, for the Milky Way's clouds and lanes. Deterministic, and periodic
// along the band (every `px` cells) so the ring closes without a seam.
function noise2(x, y, seed, px) {
  const i = Math.floor(x), j = Math.floor(y);
  const fx = x - i, fy = y - j;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const i0 = ((i % px) + px) % px, i1 = (i0 + 1) % px;
  const a = hash2(i0, j, seed), b = hash2(i1, j, seed);
  const c = hash2(i0, j + 1, seed), d = hash2(i1, j + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
// `u` runs 0..1 once round the band; `cells` is the coarsest octave's count.
function fbm2(u, y, seed, cells) {
  let v = 0, amp = 0.5, norm = 0, k = cells;
  for (let o = 0; o < 4; o++) {
    v += amp * noise2(u * k, y * (k / cells) - o * 9.1, seed + o, k);
    norm += amp;
    k *= 2;
    amp *= 0.5;
  }
  return v / norm;
}

const PLACE = /* glsl */ `
uniform mat3 uRot;
// The direction, turned with the sky, placed at the far plane: everything
// else in the frame is in front of it.
vec4 skyPlace(vec3 dir, out vec3 w) {
  w = uRot * dir;
  vec3 d = (viewMatrix * vec4(w, 0.0)).xyz;
  vec4 p = projectionMatrix * vec4(d, 1.0);
  p.z = p.w;
  return p;
}
`;

const STAR_VERT = /* glsl */ `
${PLACE}
uniform float uLimit;   // faintest magnitude showing
uniform float uPx;      // device pixels per CSS pixel
uniform float uTime;
uniform float uStep;
uniform float uGain;    // contrast against a lighter sky (see update)
attribute float aMag;
attribute float aSeed;
attribute vec3 color;
varying vec3 vCol;
varying float vCore;
void main() {
  vec3 w;
  gl_Position = skyPlace(position, w);
  // Low stars shine through more air: dimmer (about a magnitude at 5°) and
  // gone below the horizon — the terrain hides them where it reaches up.
  float up = max(w.y, 0.0);
  float mag = aMag + 0.12 / (up + 0.1);
  float show = smoothstep(uLimit + 0.4, uLimit - 0.6, mag) * smoothstep(-0.01, 0.03, w.y);
  // Twinkle: two beating flickers, deepest near the horizon.
  float tw = sin(uTime * (5.0 + 4.0 * aSeed) + aSeed * 61.0) * sin(uTime * (2.3 + 3.0 * aSeed) + aSeed * 23.0);
  float bright = uGain * pow(uStep, 1.0 - mag) * (1.0 + (0.12 + 0.4 * (1.0 - up)) * tw) * show;
  // A bright star is a fat soft point, a faint one a pinprick — no smaller
  // than a pixel and a half, or a 1080p screen loses it altogether.
  float px = clamp(1.6 + 1.6 * sqrt(max(bright - 0.25, 0.0)), 1.6, 5.0);
  gl_PointSize = px * uPx;
  vCore = px;
  vCol = color * min(bright, 1.0);
  if (show <= 0.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0); // off screen
}
`;

const STAR_FRAG = /* glsl */ `
uniform float uGamma;
varying vec3 vCol;
varying float vCore;
void main() {
  // Round and soft at the edge once there are pixels enough to show it.
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float a = vCore > 2.2 ? 1.0 - smoothstep(0.45, 1.0, length(q)) : 1.0;
  // Authored as screen brightness; taken back to light before the encode.
  gl_FragColor = vec4(pow(max(vCol * a, vec3(1e-6)), vec3(uGamma)), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const BAND_VERT = /* glsl */ `
${PLACE}
attribute float aGlow;
varying float vGlow;
void main() {
  vec3 w;
  gl_Position = skyPlace(position, w);
  vGlow = aGlow * smoothstep(-0.02, 0.12, w.y); // low down, it's lost in the haze
}
`;

const BAND_FRAG = /* glsl */ `
uniform float uOpacity;
uniform float uGamma;
uniform vec3 uTint;
varying float vGlow;
void main() {
  // max(): a GPU's pow() is free to hand back NaN for anything not > 0, and
  // tier 2's bloom smears one NaN pixel over the whole frame.
  gl_FragColor = vec4(pow(max(uTint * vGlow * uOpacity, vec3(1e-6)), vec3(uGamma)), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const additive = {
  transparent: true,
  depthWrite: false,
  fog: false,
  blending: THREE.AdditiveBlending,
};

/**
 * Build the stars and the Milky Way into `scene`. Returns { points, band,
 * update(state) }: update takes { t (sky clock, s), turn (the sky's angle
 * about its pole, radians), nightK, moon (0..1 bright moonlight), gray, sky
 * (the sky's colour, linear) }.
 */
export function buildStars(scene) {
  const dirs = [], cols = [], mags = [], seeds = [];
  const c = new THREE.Color();
  const pickColor = (u) => {
    let acc = 0;
    for (const [hex, w] of SPECTRA) {
      acc += w;
      if (u < acc) return c.setHex(hex);
    }
    return c.setHex(SPECTRA[SPECTRA.length - 1][0]);
  };
  const add = (v, mag, colU, seed) => {
    dirs.push(v.x, v.y, v.z);
    pickColor(colU);
    cols.push(c.r, c.g, c.b);
    mags.push(mag);
    seeds.push(seed);
  };
  const v = new THREE.Vector3();
  // The pole star: moderately bright, and the one star that never moves.
  add(CELESTIAL_POLE, 2.0, 0.5, 0.5);
  // The field: uniform over the sphere.
  for (let i = 0; i < FIELD_STARS; i++) {
    const z = hash2(i, 1, 131) * 2 - 1;
    const th = hash2(i, 2, 131) * Math.PI * 2;
    const r = Math.sqrt(1 - z * z);
    v.set(Math.cos(th) * r, z, Math.sin(th) * r);
    add(v, starMagnitude(hash2(i, 3, 131)), hash2(i, 4, 131), hash2(i, 5, 131));
  }
  // The Milky Way's grain: faint stars crowding its plane.
  const gp = GALACTIC_POLE;
  const ga = new THREE.Vector3().crossVectors(gp, new THREE.Vector3(0, 1, 0)).normalize();
  const gb = new THREE.Vector3().crossVectors(gp, ga).normalize();
  for (let i = 0; i < BAND_STARS; i++) {
    const t = hash2(i, 11, 131) * Math.PI * 2;
    const w = (hash2(i, 12, 131) + hash2(i, 13, 131) + hash2(i, 14, 131) - 1.5) * BAND_HALF * 0.9;
    v.copy(ga).multiplyScalar(Math.cos(t)).addScaledVector(gb, Math.sin(t)).addScaledVector(gp, w).normalize();
    add(v, 4.3 + hash2(i, 15, 131) * 1.5, 0.15 + hash2(i, 16, 131) * 0.6, hash2(i, 17, 131));
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(dirs, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(cols, 3));
  geo.setAttribute("aMag", new THREE.Float32BufferAttribute(mags, 1));
  geo.setAttribute("aSeed", new THREE.Float32BufferAttribute(seeds, 1));
  const rot = { value: new THREE.Matrix3() };
  const gamma = { value: 2.2 };
  const starUniforms = {
    uRot: rot,
    uLimit: { value: MAG_FAINT },
    uPx: { value: 1 },
    uTime: { value: 0 },
    uStep: { value: MAG_STEP },
    uGain: { value: 1 },
    uGamma: gamma,
  };
  const points = new THREE.Points(
    geo,
    new THREE.ShaderMaterial({ uniforms: starUniforms, vertexShader: STAR_VERT, fragmentShader: STAR_FRAG, ...additive }),
  );
  points.frustumCulled = false; // placed by its shader, not its transform
  points.name = "stars";
  // Point sizes are in device pixels: a star is the same size on a retina
  // screen as on any other.
  points.onBeforeRender = (renderer) => {
    starUniforms.uPx.value = renderer.getPixelRatio();
  };
  scene.add(points);

  // The Milky Way's glow: a ribbon round its great circle, its brightness
  // baked per vertex — clumps along it, a Gaussian across it, and the dark
  // rift a little off its centre line where dust blots out the light.
  const SEG = 360, ACROSS = 18;
  const bpos = [], bglow = [], idx = [];
  for (let i = 0; i <= SEG; i++) {
    const t = (i / SEG) * Math.PI * 2;
    // Wrap the noise's along-coordinate so the ring closes without a seam.
    const at = i / SEG;
    const wob = 0.25 * (fbm2(at, 0.5, 21, 6) - 0.5); // the band meanders
    for (let j = 0; j <= ACROSS; j++) {
      const s = (j / ACROSS) * 2 - 1; // -1..1 across the band
      const off = (s + wob) * BAND_HALF;
      v.copy(ga).multiplyScalar(Math.cos(t)).addScaledVector(gb, Math.sin(t)).addScaledVector(gp, off).normalize();
      bpos.push(v.x, v.y, v.z);
      const across = Math.exp(-((s / 0.55) ** 2));
      // Star clouds: brighter knots and dimmer gaps, streaked along the band.
      const cloud = fbm2(at, s * 2.2, 22, 28);
      const clump = 0.2 + 0.8 * Math.max(0, cloud * 1.6 - 0.45) ** 1.3;
      // The great rift: a dark dust lane along one side of the middle,
      // coming and going along the band.
      const lane = Math.max(0, fbm2(at, 0.3, 23, 10) * 2 - 0.8);
      const dust = 1 - Math.min(0.9, lane * 1.4) * Math.exp(-(((s - 0.12) / 0.22) ** 2));
      bglow.push(across * clump * dust * (j === 0 || j === ACROSS ? 0 : 1));
    }
  }
  for (let i = 0; i < SEG; i++) {
    for (let j = 0; j < ACROSS; j++) {
      const a = i * (ACROSS + 1) + j, b = a + ACROSS + 1;
      idx.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const bgeo = new THREE.BufferGeometry();
  bgeo.setAttribute("position", new THREE.Float32BufferAttribute(bpos, 3));
  bgeo.setAttribute("aGlow", new THREE.Float32BufferAttribute(bglow, 1));
  bgeo.setIndex(idx);
  const bandUniforms = {
    uRot: rot,
    uOpacity: { value: 0 },
    uGamma: gamma,
    uTint: { value: new THREE.Color(0xdde2f2) },
  };
  const band = new THREE.Mesh(
    bgeo,
    new THREE.ShaderMaterial({
      uniforms: bandUniforms, vertexShader: BAND_VERT, fragmentShader: BAND_FRAG,
      side: THREE.DoubleSide, ...additive,
    }),
  );
  band.frustumCulled = false;
  band.name = "milky-way";
  scene.add(band);

  const m4 = new THREE.Matrix4();
  return {
    points,
    band,
    // Tier 1 blends onto the screen, so starlight is authored as screen
    // brightness and taken back to light (2.2) before the encode. Tier 2
    // (gfx2.js) adds light to light in its HDR frame, where the same faint
    // glow on a near-black sky would all but vanish; a shallower curve
    // keeps the Milky Way and the faint stars what tier 1 shows.
    set linear(on) {
      gamma.value = on ? LINEAR_GAMMA : 2.2;
    },
    update({ t, turn, nightK, moon, gray, sky }) {
      rot.value.setFromMatrix4(m4.makeRotationAxis(CELESTIAL_POLE, turn));
      starUniforms.uTime.value = t % 3600;
      starUniforms.uLimit.value = limitingMagnitude(nightK, moon, gray);
      // Starlight is added to the sky, so against the naval theater's lighter
      // night navy the same star reads fainter than over the air game's ink.
      // The eye adapts: the stars that show hold their contrast.
      if (sky) {
        const luma = 0.2126 * sky.r + 0.7152 * sky.g + 0.0722 * sky.b; // linear
        starUniforms.uGain.value = Math.min(2, 1 + 2.2 * Math.sqrt(luma));
      }
      // The glow needs a properly dark sky: no moon, no cloud, no twilight.
      const dark = Math.max(0, (nightK - 0.7) / 0.3);
      bandUniforms.uOpacity.value = BAND_GLOW * dark * dark * (1 - 0.5 * moon) * Math.max(0, 1 - 1.8 * gray);
      points.visible = nightK > 0.02;
      band.visible = bandUniforms.uOpacity.value > 0.002;
    },
  };
}
